import i18n, { i18n as I18n } from "i18next";
import {
  brandBaseOf,
  brandBases,
  FALLBACK,
  getFullLocale,
} from "@topi/ui/utils/locale";
import { CATALOGS } from "./catalogs";

// Locale helpers kept apart from the i18next setup, so hooks and slices can
// use them without loading it. The ones that don't need i18next live in `ui`.

export {
  brandBaseOf,
  brandBases,
  detectLocales,
  FALLBACK,
  formatLocaleFor,
  getFullLocale,
} from "@topi/ui/utils/locale";

/** The languages i18next accepts, as `supportedLngs`. */
export const SUPPORTED_LANGUAGES = ["en", "en-GB", "de", "nl"];

// With `nonExplicitSupportedLngs`, i18next only checks the language part.
const isSupported = (language: string): boolean =>
  SUPPORTED_LANGUAGES.includes(language.split(/[-_]/)[0].toLowerCase());

/**
 * The i18next language for a brand: the most specific of its Lokalise custom
 * ISO codes that has a catalog, such as `en_us_peac_us_inc`, then
 * `en_peac_us_inc`. It stays in the browser's language, and is built on `en`
 * only when i18next doesn't support that language. Without a brand code, or
 * when the brand has no catalog in that language, it is the locale itself.
 */
export const languageFor = (locale: string, code: string | null): string => {
  if (!code) {
    return locale;
  }
  const bases = brandBases(locale);
  const language = bases[0].split("_")[0];
  const candidates = isSupported(language)
    ? bases.filter((base) => base.split("_")[0] === language)
    : [FALLBACK];
  return (
    candidates
      .map((base) => `${base}_${code}`)
      .find((candidate) => CATALOGS.has(candidate)) ?? locale
  );
};

/** How long the brand catalogs may take before the switch gives up. */
export const BRAND_CATALOG_TIMEOUT_MS = 10_000;

// `changeLanguage` never rejects: a catalog that fails resolves after
// i18next's retries, and one that hangs never resolves.
const changeLanguageWithin = async (
  instance: I18n,
  language: string,
  timeoutMs: number
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(
      () => reject(new Error(`Brand language ${language} timed out`)),
      timeoutMs
    );
  });
  try {
    await Promise.race([instance.changeLanguage(language), timeout]);
  } finally {
    clearTimeout(timer);
  }
};

/**
 * Switches to the brand's language once its branding arrives, and resolves
 * when its catalogs have loaded. The topi brand has no code: it keeps the
 * current language, or leaves a brand language for the browser locale.
 * Rejects when a brand catalog the build ships doesn't load in time, so the
 * page shows its error state instead of topi copy.
 */
export const switchToBrandLanguage = async (
  code: string | null,
  instance: I18n = i18n,
  timeoutMs: number = BRAND_CATALOG_TIMEOUT_MS
): Promise<void> => {
  if (!instance.isInitialized && !instance.isInitializing) {
    return;
  }
  const locale = getFullLocale();
  const isBrand = !!instance.language && !!brandBaseOf(instance.language);
  if (!code && !isBrand) {
    return;
  }
  const language = languageFor(locale, code);
  if (instance.language === language) {
    return;
  }
  await changeLanguageWithin(instance, language, timeoutMs);
  const missing = instance.languages.filter(
    (lng) =>
      !!brandBaseOf(lng, locale) &&
      CATALOGS.has(lng) &&
      !instance.hasResourceBundle(lng, "translation")
  );
  if (missing.length > 0) {
    throw new Error(`Brand catalogs didn't load: ${missing.join(", ")}`);
  }
};

/** The brand's Lokalise language code; topi has none. */
export const brandLanguageCode = (shortName?: string): string | null =>
  shortName && shortName !== "topi" ? shortName : null;

/**
 * Resolves with `branding` once the brand's language has loaded, so a page
 * that waits for the branding renders the brand copy from the start.
 */
export const withBrandLanguage = async <T extends { shortName?: string }>(
  branding: T
): Promise<T> => {
  await switchToBrandLanguage(brandLanguageCode(branding.shortName));
  return branding;
};
